import { useState } from "react";
import { signal } from "@preact/signals-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ChevronDownIcon, EyeIcon } from "components/Icons";
import { cn } from "tools/ui";
import LabelKind from "./ui/LabelKind";
import LabelTime from "./ui/LabelTime";
import OptionsCard from "componentsv2/kanban/OptionsCard";

function HeaderCard({ number, table, color, deployCard }) {
  return (
    <div
      className={cn(
        "flex justify-between px-3 pt-2 mb-0 pb-2 rounded-t-md",
        color?.card,
        deployCard.value && "mb-2",
      )}
    >
      <span className="rounded text-md text-black">
        PED:
        <span className="font-bold ml-1"> {number} </span>
      </span>

      <div className="flex space-x-1 items-center">
        {table?.rec_name ? (
          <span className="rounded text-md text-black">
            MESA:
            <span className="font-bold ml-1">{table?.rec_name}</span>
          </span>
        ) : (
          <LabelKind kind="delivery" />
        )}
        <OptionsCard options={[]} deployCard={deployCard} />
      </div>
    </div>
  );
}

function CardStatic({ record, color, handleModal, deployCard }) {
  // const [deployCard, setDeployCard] = useState(false);
  const bgColor = "bg-white";

  const {
    number,
    "lines.": lines,
    "table_assigned.": table,
    "order_status_time.": order_status_time,
  } = record;

  return (
    <div
      className={cn(
        "relative mb-2 shadow-md/2 space-y-3 w-full cursor-move rounded-md border border-gray-400",
        bgColor,
      )}
    >
      <HeaderCard
        number={number}
        table={table}
        color={color}
        deployCard={deployCard}
      />
      {deployCard.value && (
        <div
          className={cn(
            "divide-y divide-dashed divide-neutral-200 border border-dashed border-gray-200 mx-2 bg-neutral-100 px-3",
          )}
        >
          {lines?.map((line) => (
            <div key={line.id} className="py-2 text-xs">
              <span>
                <span className="font-semibold">{line.quantity}</span> x{" "}
                {line?.["product."].name}
              </span>

              {line?.note && (
                <p className="mt-2 rounded-md">
                  <span className="font-semibold mt-1 pl-2">- {line.note}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <FooterCard
        item={record}
        color={color}
        handleModal={handleModal}
        deployCard={deployCard}
      />
    </div>
  );
}

function FooterCard({ item, color, handleModal, deployCard }) {
  const time = item["order_status_time."]?.commanded ?? item.pos_create_date;

  return (
    <div className="px-3 py-2 bg-white rounded-b-md border-t border-gray-200">
      {item.description && (
        <div className="text-xs text-gray-600">
          <div>
            NOTA:
            <span className="font-bold ml-1">{item.description ?? ""}</span>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center text-md">
        <LabelTime time={time} />

        <div className="flex space-x-2 text-gray-500">
          <button type="button" onClick={() => handleModal(item)}>
            <EyeIcon className="size-5 fill-current " />
          </button>
          <button
            className={cn(
              "transition-transform duration-200",
              deployCard.value && "rotate-180",
            )}
            onClick={() => (deployCard.value = !deployCard.value)}
          >
            <ChevronDownIcon className={cn("fill-current size-5")} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CardItem({
  record,
  state,
  // model,
  color,
  isOverlay,
  handleModal,
  // activeRecord,
}) {
  const deployCard = signal(false);

  if (isOverlay) {
    return (
      <CardStatic
        record={record}
        color={color}
        handleModal={handleModal}
        deployCard={deployCard}
      />
    );
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: record.id,
    data: {
      record,
      column: state,
    },
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? "transform 200ms cubic-bezier(.2,.8,.2,1)",
    zIndex: isDragging ? 9999 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CardStatic
        record={record}
        color={color}
        handleModal={handleModal}
        deployCard={deployCard}
      />
    </div>
  );
}

export default CardItem;
